import {
  type UsersListQuerySchema,
  type EffectivePermissions,
  type Paginated,
  type ReviewedAccessSaveRequest,
  type Session,
  type User,
  type UserDetail,
} from '@aysar/contracts';
import { createApi } from '@reduxjs/toolkit/query/react';
import type { z } from 'zod';

import { EVENT_BUS } from '@/lib/event-bus';
import { baseQueryWithAuth } from '@/shared/store/middleware/base-query-with-auth/base-query-with-auth';
import { type ApiSubscriber } from '@/shared/store/registry/api-registry/api-registry.types';

type StaffUsersParams = z.input<typeof UsersListQuerySchema>;

/**
 * Staff-facing account administration — the reads, the access editor and the
 * sessions.
 *
 * The lifecycle writes (suspend / restore / ban / status) left this api in
 * P27c1 for the shared `accountStatusApi`: two modules now make them through
 * the same kit dialogs, and a kit dialog may import no module. This api hears
 * about them on `members.status.changed` — see {@link subscribeStaffUsers} —
 * and refreshes its own rows, which is how a write in another slice reaches a
 * list in this one (W5).
 */
export const staffUsersApi = createApi({
  reducerPath: 'staffUsersApi',
  baseQuery: baseQueryWithAuth,
  tagTypes: ['StaffUser', 'StaffUserList', 'UserAccess', 'StaffSessions'],
  endpoints: (builder) => ({
    listStaffUsers: builder.query<Paginated<User>, StaffUsersParams>({
      query: (params) => ({ url: '/admin/users', method: 'GET', params }),
      providesTags: ['StaffUserList'],
    }),

    staffUserDetail: builder.query<UserDetail, string>({
      query: (id) => ({ url: `/admin/users/${id}`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'StaffUser' as const, id }],
    }),

    /**
     * The effective-permissions preview: `(roles ∪ grants) − denies`, wildcards
     * expanded, as the api computes it.
     *
     * Confined by N28 — a `target_outranks_actor` 403 here is the api refusing to
     * enumerate a stronger colleague's authority, which the detail page renders as
     * a state rather than as an error.
     */
    userAccess: builder.query<EffectivePermissions, string>({
      query: (id) => ({ url: `/admin/users/${id}/access`, method: 'GET' }),
      providesTags: (_result, _error, id) => [{ type: 'UserAccess' as const, id }],
    }),

    /**
     * The **reviewed save**: an operator's whole intent — roles *and* carve-outs —
     * as one request, judged once against the resulting context and written in one
     * transaction (P10.6g).
     *
     * Both collections are **complete sets**: a role or a carve-out is removed by
     * being left out, which is idempotent and cannot lose an update the way a
     * delta can. The api keeps `member` attached itself, so the portal never sends
     * it and can never drop it by accident.
     *
     * It answers with the recomputed `EffectivePermissions`, so nothing is re-read
     * after a save. A **409 `concurrent_access_change`** means someone else edited
     * the account between the read this draft was built on and the write — nothing
     * was saved, and the surface says so rather than reloading over the operator's
     * work.
     */
    saveAccess: builder.mutation<
      EffectivePermissions,
      { id: string; body: ReviewedAccessSaveRequest }
    >({
      query: ({ id, body }) => ({ url: `/admin/users/${id}/access`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, { id }) => [
        'StaffUserList',
        { type: 'StaffUser' as const, id },
        { type: 'UserAccess' as const, id },
      ],
      async onQueryStarted({ id }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'access.staff.changed', userId: id });
        } catch {
          // See `changeUserStatus`.
        }
      },
    }),

    /**
     * Force-logout for one account. There is no batch route, so the bulk action
     * loops — which is safe precisely because revoking is **idempotent**:
     * bumping an already-bumped token version changes nothing, so a partial
     * failure can simply be retried over the whole selection.
     */
    revokeSessions: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/users/${id}/access/revoke-sessions`, method: 'POST' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'StaffUser' as const, id },
        // It ends every row, so it invalidates the list of them. It could not
        // say so before `staffSessions` existed to provide the tag.
        { type: 'StaffSessions' as const, id },
      ],
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'access.staff.changed', userId: id });
        } catch {
          // The mutation was refused, so there is nothing to announce. Swallowed
          // deliberately: `onQueryStarted` has no caller, so a rejection escaping
          // it becomes an unhandled rejection — which is how this surfaced, as a
          // flaky test run rather than as an error anyone could act on. The
          // failure itself is already the caller's, through `unwrap()`.
        }
      },
    }),

    /**
     * The devices signed in to one account — **live rows only, and unpaged**.
     *
     * A live refresh-token row *is* a signed-in device (rotation chains the
     * superseded ones behind it), so the set cannot fill a page and there is
     * nothing to paginate. It carries no token and no hash: `Session` has no
     * field to put one in (N2).
     */
    staffSessions: builder.query<Session[], string>({
      query: (id) => ({ url: `/admin/users/${id}/sessions`, method: 'GET' }),
      providesTags: (_r, _e, id) => [{ type: 'StaffSessions' as const, id }],
    }),

    /**
     * Ending ONE device.
     *
     * Announced as an access change rather than a status change, for the same
     * reason the force-logout is: the account's lifecycle status has not moved.
     *
     * Deliberately does **not** invalidate `StaffUser` — unlike the force-logout,
     * this changes nothing the identity header renders. And a **404 is a real
     * answer**: the row was already gone, which is the outcome the operator
     * wanted. Nothing is announced then, because nothing changed; the caller
     * reports it and refetches, since `invalidatesTags` does not run on a
     * rejected mutation.
     */
    revokeSession: builder.mutation<void, { id: string; sessionId: string }>({
      query: ({ id, sessionId }) => ({
        url: `/admin/users/${id}/sessions/${sessionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'StaffSessions' as const, id }],
      async onQueryStarted({ id }, { queryFulfilled }) {
        try {
          await queryFulfilled;
          EVENT_BUS.publish({ type: 'access.staff.changed', userId: id });
        } catch {
          // See `revokeSessions`.
        }
      },
    }),
  }),
});

/**
 * The list and the identity header both draw the lifecycle status, and the
 * write that moves it is no longer this slice's — so the announcement is what
 * invalidates them. Wired by the store at construction, so the refresh happens
 * whether or not a staff page is currently open.
 */
export const subscribeStaffUsers: ApiSubscriber = (dispatch) =>
  EVENT_BUS.subscribeTo('members.status.changed', ({ userId }) => {
    dispatch(
      staffUsersApi.util.invalidateTags(['StaffUserList', { type: 'StaffUser', id: userId }]),
    );
  });

export const {
  useListStaffUsersQuery,
  useStaffUserDetailQuery,
  useUserAccessQuery,
  useSaveAccessMutation,
  useRevokeSessionsMutation,
  useStaffSessionsQuery,
  useRevokeSessionMutation,
} = staffUsersApi;
